import { HatchName } from "./hatches/hatch-list";
import { DEFAULT_FONT } from "./config/fonts";
import type {
  Category,
  Legend,
  LegendSettings,
  NoteConfig,
  ScaleConfig,
} from "./types/legend.types";
import { LEGEND_VERSION } from "./utils/constants";
import type { PhotoMarkerShape } from "@/drawing";
import { v4 } from "uuid";
import { LAYER_DEFAULT_LINEWEIGHT } from "@/drawing/lineweight/utils";

export const DEFAULT_FAVORITE_PHOTO_MARKERS: PhotoMarkerShape[] = [
  "circle",
  "thin_arrow",
];

export const PHOTO_MARKER_DATE_FORMAT_PRESETS = [
  "HH:mm:ss",
  "HH:mm",
  "dd/MM HH:mm",
  "dd/MM/yyyy",
] as const;

export const DEFAULT_LEGEND_SETTINGS: LegendSettings = {
  favorite_photo_markers: DEFAULT_FAVORITE_PHOTO_MARKERS,
  photo_marker_mode: "insert",
  photo_marker_attribute: "number",
  photo_marker_date_format: PHOTO_MARKER_DATE_FORMAT_PRESETS[0],
};
// Notes brutes : `categories` (noms) et `layername` sont des helpers d'écriture, retirés
// avant d'atterrir dans note_configs — l'appartenance aux catalogues est portée par
// Category.noteIds, et la référence au calque par props.layerId (voir plus bas).
const RAW_NOTES: Array<
  Omit<NoteConfig, "id"> & { categories?: string[]; layername?: string }
> = [
    {
      name: "Fissure",
      categories: ["Béton"],
      type: "default",
      label: "Fi",
      layername: "DRFissures",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Fracture",
      categories: ["Béton"],
      type: "default",
      label: "Fr",
      layername: "DRFissures",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "micro-fissure",
      categories: ["Béton"],
      type: "default",
      label: "µFi",
      layername: "DRFissures",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Faïençage",
      categories: ["Béton"],
      type: "default",
      label: "Fa",
      layername: "DRFissures",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Végétation",
      categories: ["Végétation"],
      type: "default",
      label: "Vég",
      hatch: { name: HatchName.GRASS },
      layername: "DRVégétation",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Arbre",
      categories: ["Végétation"],
      type: "default",
      label: "Arbre",
      layername: "DRVégétation",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Mousse",
      categories: ["Végétation"],
      type: "default",
      label: "Mo",
      layername: "DRVégétation",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Traces d'humidité",
      categories: ["Humidité"],
      type: "default",
      label: "Hum",
      hatch: { name: HatchName.HUM },
      layername: "DRHumidité",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Stalactite",
      categories: ["Humidité"],
      type: "default",
      label: "Sta",
      layername: "DRHumidité",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Calcite",
      categories: ["Humidité"],
      type: "default",
      label: "Cal",
      hatch: { name: HatchName.HUM },
      layername: "DRHumidité",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Eclat",
      categories: ["Béton", "Maçonnerie"],
      type: "default",
      label: "Ec",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Corrosion",
      categories: ["Béton", "Métal"],
      type: "default",
      label: "Co",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Eclat en formation",
      categories: ["Béton"],
      label: "EcF",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kBylayer",
      },
    },
    {
      name: "Epaufrure",
      categories: ["Béton", "Maçonnerie"],
      type: "default",
      label: "Epf",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Aciers apparents oxydés",
      categories: ["Béton"],
      type: "default",
      label: "AAO",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Nid de Cailloux",
      categories: ["Béton"],
      type: "default",
      label: "NdC",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Ségrégation",
      categories: ["Béton"],
      type: "default",
      label: "Ség",
      layername: "DRBéton",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Bombement",
      label: "Bomb",
      type: "default",
      categories: ["Général"],
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Déversement",
      label: "Dév",
      type: "default",
      categories: ["Général"],
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Eraflures",
      label: "Era",
      type: "default",
      categories: ["Général"],
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Divers",
      label: "Divers",
      type: "default",
      categories: ["Général"],
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Corrosion",
      categories: ["Métal"],
      type: "default",
      label: "Cor",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Déchaussement",
      categories: ["Maçonnerie"],
      type: "default",
      label: "Dech",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Lacune",
      categories: ["Maçonnerie"],
      type: "default",
      label: "Lac",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Erosion",
      categories: ["Maçonnerie", "Abords"],
      type: "default",
      label: "Ero",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Disjointement",
      categories: ["Maçonnerie"],
      type: "default",
      label: "Disj",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Tassement",
      categories: ["Chaussée"],
      type: "default",
      label: "Tas",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Nid de Poule",
      categories: ["Chaussée"],
      type: "default",
      label: "NdP",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Ravinement",
      categories: ["Abords"],
      type: "default",
      label: "Rav",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Affouillement",
      categories: ["Abords"],
      type: "default",
      label: "Aff",
      layername: "DRDésordres",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Photos",
      categories: [],
      type: "photo",
      layername: "DRPhotos",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Notes",
      categories: ["Général"],
      type: "default",
      layername: "DRNotes",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Dépots divers",
      categories: ["Général"],
      label: "Depots",
      type: "default",
      layername: "DRNotes",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Ragréage",
      categories: ["Réparations"],
      label: "Rg",
      type: "default",
      layername: "DRRéparations",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
    {
      name: "Reprise de bétonnage",
      categories: ["Réparations"],
      label: "RB",
      type: "default",
      layername: "DRRéparations",
      props: {
        model: "ACTIVE",
        color: "kByLayer",
      },
    },
];

// Calques du gabarit : id stable généré une fois au chargement du module, référencé par
// les notes via props.layerId plutôt que par nom (voir NOTES_WITH_IDS ci-dessous).
const LAYER_DEFS = [
  { name: "DRFissures", color: "255,0,0" },
  { name: "DRDésordres", color: "255,140,0" },
  { name: "DRBéton", color: "125,255,0" },
  { name: "DRHumidité", color: "0,0,255" },
  { name: "DRVégétation", color: "0,255,0" },
  { name: "DRPhotos", color: "255,191,0" },
  { name: "DRNotes", color: "125,125,125" },
  { name: "DRRéparations", color: "0,255,127" },
];
const LAYERS_WITH_IDS = LAYER_DEFS.map((layer) => ({
  id: v4(),
  name: layer.name,
  visibility: true,
  color: layer.color,
  linetype: "Continuous",
  lineweight: LAYER_DEFAULT_LINEWEIGHT,
}));
const LAYER_ID_BY_NAME = new Map(LAYERS_WITH_IDS.map((l) => [l.name, l.id]));

const NOTES_WITH_IDS = RAW_NOTES.map((note) => ({
  ...note,
  id: v4(),
  props: {
    ...note.props,
    layerId: note.layername ? LAYER_ID_BY_NAME.get(note.layername) : undefined,
  },
}));

const CATEGORY_NAMES = [
  "Béton",
  "Végétation",
  "Humidité",
  "Maçonnerie",
  "Métal",
  "Général",
  "Chaussée",
  "Abords",
  "Réparations",
];
const DEFAULT_CATEGORIES: Category[] = CATEGORY_NAMES.map((name) => ({
  id: v4(),
  name,
  noteIds: NOTES_WITH_IDS.filter((n) => n.categories?.includes(name)).map((n) => n.id),
}));

export const DEFAULT_LEGEND: Legend = {
  id: 0,
  name: "DR",
  version: LEGEND_VERSION,
  note_configs: NOTES_WITH_IDS.map(({ categories, layername, ...note }) => note),
  layer_configs: LAYERS_WITH_IDS,
  scale_configs: [
    {
      id: v4(),
      name: "1-50",
      props: {
        textstyle: {
          name: "DR 1-50",
          filename: DEFAULT_FONT.filename,
          size: 0.1,
        },
      },
    },
    {
      id: v4(),
      name: "1-75",
      props: {
        textstyle: {
          name: "DR 1-75",
          filename: DEFAULT_FONT.filename,
          size: 0.15,
        },
      },
    },
    {
      id: v4(),
      name: "1-100",
      props: {
        textstyle: {
          name: "DR 1-100",
          filename: DEFAULT_FONT.filename,
          size: 0.2,
        },
      },
    },
    {
      id: v4(),
      name: "1-125",
      props: {
        textstyle: {
          name: "DR 1-125",
          filename: DEFAULT_FONT.filename,
          size: 0.25,
        },
      },
    },
    {
      id: v4(),
      name: "1-150",
      props: {
        textstyle: {
          name: "DR 1-150",
          filename: DEFAULT_FONT.filename,
          size: 0.3,
        },
      },
    },
    {
      id: v4(),
      name: "1-200",
      props: {
        textstyle: {
          name: "DR 1-200",
          filename: DEFAULT_FONT.filename,
          size: 0.4,
        },
      },
    },
    {
      id: v4(),
      name: "1-250",
      props: {
        textstyle: {
          name: "DR 1-250",
          filename: DEFAULT_FONT.filename,
          size: 0.5,
        },
      },
    },
    {
      id: v4(),
      name: "1-300",
      props: {
        textstyle: {
          name: "DR 1-300",
          filename: DEFAULT_FONT.filename,
          size: 0.8,
        },
      },
    },
    {
      id: v4(),
      name: "1-600",
      props: {
        textstyle: {
          name: "DR 1-600",
          filename: DEFAULT_FONT.filename,
          size: 1.2,
        },
      },
    },
  ],
  categories: DEFAULT_CATEGORIES,
  settings: DEFAULT_LEGEND_SETTINGS,
};

// Fallback autonome (utilisé quand aucune note de type "photo" n'existe dans la légende
// active) : pas de layerId, puisqu'il n'est rattaché à aucun layer_configs particulier
// dont l'id serait résolvable — contrairement à un layername, un layerId inventé ici ne
// correspondrait à aucun calque réel.
export const DEFAULT_PHOTO_NOTE_CONFIG: NoteConfig = {
  id: v4(),
  name: "Photos",
  type: "photo",
  props: {
    model: "ACTIVE",
    color: "kByLayer",
  },
};

export const DEFAULT_NOTE_CONFIG: NoteConfig = {
  id: v4(),
  name: "DEFAULT",
  props: {
    model: "ACTIVE",
    color: "255,0,0",
    textstyle: {
      name: "default",
      size: 0.2,
      filename: DEFAULT_FONT.filename,
    },
  },
};

export const DEFAULT_SCALE: ScaleConfig = {
  id: v4(),
  name: "DR_SCALE_CONFIG",
  props: {
    textstyle: {
      name: "DR_TEXTSTYLE",
      filename: DEFAULT_FONT.filename,
      size: 0.5,
    },
  },
};
